import React, { useEffect, useMemo, useState } from "react";
import { usePlanner } from "../../contexts/PlannerContext";
import { usePricingSettings } from "../../contexts/PricingSettingsContext";
import { useShopOffersSummary } from "../../hooks/useShopOffersSummary";
import { apiFetch } from "../../lib/apiFetch";

function isWithinRange(dateStr, from, to) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d >= from && d <= to;
}

export function DashboardStudioCoachCard({ variant = "full" }) {
  const { events = [] } = usePlanner() || {};
  const { pricingSettings } = usePricingSettings();
  const { openOffersCount, staleOffersCount } = useShopOffersSummary();

  const [aiCoach, setAiCoach] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const isCompact = variant === "compact";
  const cardPadding = isCompact ? "p-3" : "p-4 md:p-5";
  const titleClass = isCompact ? "text-xs font-medium opacity-90" : "mudo-section-title";
  const summaryClass = isCompact ? "text-[11px] opacity-80" : "text-xs opacity-80";

  const aiEnabled =
    pricingSettings?.enableScheduleAi !== false ||
    pricingSettings?.enableKilnAi !== false ||
    pricingSettings?.enableOfferAi !== false ||
    pricingSettings?.enablePricingAi !== false;

  const summaryContext = useMemo(() => {
    const now = new Date();
    const sevenDays = new Date();
    sevenDays.setDate(now.getDate() + 7);

    const upcomingTasks = [];
    const firings = [];

    (events || []).forEach((ev) => {
      const start = ev.start || ev.startDate || ev.date;
      if (!isWithinRange(start, now, sevenDays)) return;
      const base = {
        id: ev.id,
        title: ev.title,
        type: ev.type || ev.category || null,
        startDate: start,
        endDate: ev.end || ev.endDate || ev.start || ev.startDate || start,
        tags: ev.tags || [],
      };
      upcomingTasks.push(base);
      const tagStr = (ev.tags || []).join(" ").toLowerCase();
      if (
        (ev.type && String(ev.type).toLowerCase().includes("kiln")) ||
        tagStr.includes("kiln") ||
        tagStr.includes("firing")
      ) {
        firings.push(base);
      }
    });

    const pricingHighlights = [];

    return {
      upcomingTasks,
      firings,
      offers: {
        openCount: openOffersCount || 0,
        staleCount: staleOffersCount || 0,
      },
      pricingHighlights,
      settings: {
        targetRetailMargin: pricingSettings?.targetRetailMargin,
        targetWholesaleMargin: pricingSettings?.targetWholesaleMargin,
        minHourlyRate: pricingSettings?.minHourlyRate,
      },
    };
  }, [events, openOffersCount, staleOffersCount, pricingSettings]);

  async function fetchStudioCoach() {
    if (!aiEnabled) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await apiFetch("/api/ai/coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "studio", context: summaryContext }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "AI error");
      setAiCoach(json.result);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("[DashboardStudioCoach] AI error", err);
      setAiError("Mudo AI couldn't summarise the studio right now.");
    } finally {
      setAiLoading(false);
    }
  }

  useEffect(() => {
    fetchStudioCoach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subtitle = useMemo(() => {
    if (!lastRefreshed) return "This week at a glance";
    return `Updated ${lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }, [lastRefreshed]);

  return (
    <div className={`mudo-card ${cardPadding} space-y-3`}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className={titleClass}>Mudo AI studio coach</h2>
          <p className={summaryClass}>{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={fetchStudioCoach}
          disabled={aiLoading || !aiEnabled}
          className={`text-[11px] ${isCompact ? "px-2 py-1" : "px-3 py-1.5"} rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-label="Refresh studio coach"
        >
          {aiLoading ? (isCompact ? "…" : "Refreshing…") : isCompact ? "↻" : "Refresh"}
        </button>
      </div>

      {!aiEnabled && (
        <p className={summaryClass}>
          Studio AI is disabled. Enable it under <span className="font-medium">Customize → AI &amp; pricing</span> to see a weekly summary.
        </p>
      )}

      {aiError && <p className={isCompact ? "text-[11px] text-red-300" : "text-xs text-red-300"}>{aiError}</p>}

      {aiCoach && !aiError && (
        <div className="space-y-1 text-sm">
          <div className={isCompact ? "text-[11px] opacity-85" : "font-medium opacity-90"}>{aiCoach.title}</div>
          <p className={summaryClass}>{aiCoach.summary}</p>
          {!isCompact && Array.isArray(aiCoach.bullets) && aiCoach.bullets.length > 0 && (
            <ul className="mt-1 text-xs opacity-85 space-y-1 list-disc list-inside">
              {aiCoach.bullets.map((b, idx) => (
                <li key={idx}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!aiCoach && !aiLoading && !aiError && aiEnabled && (
        <p className={summaryClass}>
          {isCompact
            ? "Mudo will summarise your week here once there are upcoming tasks or offers."
            : "Mudo will show a short weekly summary here once there are upcoming tasks, firings or offers."}
        </p>
      )}
    </div>
  );
}
